import React from 'react';

import { BloodworkDisplay } from '../components/bloodworkz/bloodwork-display';

import { observer, inject } from 'mobx-react';
import { BloodworkEntryStore } from '../stores/bloodwork-entry-store';
import { ClientEntryStore } from '../stores/client-entry-store';
import { FurtherConsiderationsEntryStore } from '../stores/further-considerations-entry-store';
import { UserProfileUIStore } from '../stores/user-profile-ui-store';
import { HeldLabResults } from '../components/client/held-lab-results';
import { LAB_INTEGRATION_VIRTUAL_CLINIC } from '../models/UserProfile';
import { visibleExceptProd } from '../utils/featureVisibility';

interface BloodworkDisplayContainerProps {
  userProfileUIStore?: UserProfileUIStore;
  bloodworkEntryStore?: BloodworkEntryStore;
  clientEntryStore?: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  history?: any;
}

@inject(
  'bloodworkEntryStore',
  'clientEntryStore',
  'furtherConsiderationsEntryStore',
  'userProfileUIStore',
)
@observer
export class BloodworkDisplayContainer extends React.Component<
  BloodworkDisplayContainerProps,
  {}
> {
  componentWillMount() {
    const {
      userProfileUIStore,
      furtherConsiderationsEntryStore,
      bloodworkEntryStore,
      clientEntryStore,
    } = this.props;
    userProfileUIStore.loadCurrentUserProfile();
    furtherConsiderationsEntryStore.loadFurtherConsiderations();
    bloodworkEntryStore.loadBloodworkRanges();
    if (clientEntryStore && !clientEntryStore.clients) {
      clientEntryStore.loadCurrentClients();
    }
  }

  scrollTop() {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  /**
   * Open a client's own record, the way the Clients table's edit action does.
   *
   * The same sequence, deliberately, rather than a tidier one: that screen is
   * driven by a store rather than by the route, so the client has to be selected
   * and the panel expanded after the navigation, and the second
   * setExpandAddClient is what actually opens it. See editClient in
   * client-display.tsx, which carries the note about why.
   *
   * Reached from a held lab result whose date of birth did not match, for the
   * one case the modal cannot settle: OUR record is the one with the typo. The
   * correction belongs here, with this screen's validation, and not as a button
   * that writes an outside lab's date onto a client.
   */
  openClientRecord(clientId: string) {
    const { clientEntryStore, history } = this.props;
    const client = (clientEntryStore?.clients || []).find(
      (c: any) => c.id === clientId,
    );
    if (!client || !history) return;

    clientEntryStore.clearRecentlySavedClient();
    if (history.location && history.location.pathname !== '/client/edit/manage') {
      history.push('/client/edit/manage');
    }
    clientEntryStore.setCurrentClient(client);
    clientEntryStore.setExpandAddClient(true);
    setTimeout(() => {
      clientEntryStore.setExpandAddClient(true);
      this.scrollTop();
    }, 1000);
  }

  render() {
    const {
      userProfileUIStore,
      bloodworkEntryStore,
      clientEntryStore,
      furtherConsiderationsEntryStore,
    } = this.props;

    /*
     * Results a lab sent that were not filed: a date of birth that did not match
     * the client on file, or an account at its test limit.
     *
     * On the HOME screen, above the test list, because that is where
     * practitioners actually land. It sat on the Clients page first and that was
     * wrong for the same reason it cannot sit on the client's own record: nobody
     * goes looking for a result they do not know arrived. Renders nothing at all
     * when nothing is held, which is almost always.
     */
    const profile = userProfileUIStore.currentUserProfile;
    const showHeldResults =
      visibleExceptProd() &&
      !!profile &&
      profile.allowsLabIntegration(LAB_INTEGRATION_VIRTUAL_CLINIC);

    return (
      <>
        {showHeldResults && (
          <HeldLabResults
            onReleased={() => bloodworkEntryStore.refreshBloodworks()}
            onOpenClient={clientId => this.openClientRecord(clientId)}
          />
        )}
        <BloodworkDisplay
        currentUserProfile={userProfileUIStore.currentUserProfile}
        testResultsPanelLabel="Recently Added Test Results"
        bloodworks={bloodworkEntryStore.bloodworks}
        bloodworkEntryStore={bloodworkEntryStore}
        refreshBloodworks={bloodworkEntryStore.refreshBloodworks}
        furtherConsiderations={
          furtherConsiderationsEntryStore.furtherConsiderations
        }
        allowLoadMoreTestResults={true}
        history={this.props.history}
        showAnalyzeTableButton={true}
        updateClient={clientEntryStore ? (client) => clientEntryStore.updateClientFields(client) : undefined}
        currentClients={clientEntryStore?.clients}
        />
      </>
    );
  }
}
